import React, { useEffect, useState, useCallback } from "react";

// Auto-advancing hero image carousel (slides left). Pauses on hover; dots to jump.
export default function HeroCarousel({ images = [], interval = 5000, className = "" }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = images.length;

  const go = useCallback((i) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    if (paused || count <= 1) return undefined;
    const id = setInterval(() => setIndex((p) => (p + 1) % count), interval);
    return () => clearInterval(id);
  }, [paused, count, interval]);

  return (
    <div
      className={`group relative h-72 overflow-hidden rounded-2xl border border-ff-border shadow-md sm:h-96 lg:h-full ${className}`}
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((src, i) => (
          <div key={i} className="h-full min-w-full overflow-hidden">
            <img
              src={src}
              alt={`Americans supporting their community ${i + 1}`}
              className={`h-full w-full object-cover ${i === index ? "ff-kenburns" : ""}`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />

      {count > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2" data-testid="hero-carousel-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white/90"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
